/* eslint-disable */
import React, { useRef, useEffect, useState } from 'react';
import { Chart, Bar } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Container, Grid, Header, Segment } from 'semantic-ui-react';
import { dbUTCDatetimeToLocalTime, dbUTCTimeToLocalDiff, polyFillStructuredClone } from '../../utils';
import { chartArmyGreen, chartBlues, chartTeals } from '../../utils/chart';
import Moment from 'moment-timezone';
import { extendMoment } from 'moment-range';

Chart.register(zoomPlugin);
Chart.register(annotationPlugin);

const moment = extendMoment(Moment);

const CHARGE_END_TIMEOUT = 120;
const FIVE_MINUTES = 300;


export const SessionChart = (
  { sessions, currents, highlightMissingReadings, ...props }
) => {
  const [datasets, setDatasets] = useState([]);
  const [xLabels, setXLabels] = useState([]);
  const [annotations, setAnnotations] = useState([]);

  let colours = [];
  colours.push([chartArmyGreen[2], chartArmyGreen[0], chartArmyGreen[1]]);
  colours.push([chartTeals[2], chartTeals[0], chartTeals[1]]);
  colours.push([chartBlues[2], chartBlues[0], chartBlues[1]]);

  let labels = ['', 'a', 'b', 'c', 'd', 'e', 'f', 'g'];

  // We need to subtract first timestamp to calibrate all the times from zero
  const timeDiff = (t1, t2) => dbUTCTimeToLocalDiff(t1, t2);

  const padSession = (originalReadings, highlightMissingReadings) => {
    let previousTimestamp = originalReadings[0].timestamp;
    let paddedReadings = [];

    for (let i = 0; i < originalReadings.length; ++i) {
      if (i > 0) {
        let oldTimestamp = moment(originalReadings[i - 1].timestamp, 'YYYY-MM-DDTHH:mm:ss,Z');
        let newTimestamp = moment(originalReadings[i].timestamp, 'YYYY-MM-DDTHH:mm:ss,Z');

        let elapsedMilliseconds = moment.duration(newTimestamp.diff(oldTimestamp))._milliseconds;
        const MIN_SECONDS_BETWEEN_READINGS = 60;
        if (elapsedMilliseconds >= MIN_SECONDS_BETWEEN_READINGS * 1000) {
          let offset = 0;
          while (offset < elapsedMilliseconds) {
            oldTimestamp.add(MIN_SECONDS_BETWEEN_READINGS, 'seconds')
            const addReadingTimestamp = moment(oldTimestamp);

            let addReading = polyFillStructuredClone(originalReadings[i - 1]);

            if (highlightMissingReadings) {
              addReading.l1_current = -1;
              addReading.l2_current = -1;
              addReading.l3_current = -1;
            }

            addReading.timestamp = addReadingTimestamp.utc().format('YYYY-MM-DDTHH:mm:ss') + ',Z';
            paddedReadings.push(addReading);

            offset += MIN_SECONDS_BETWEEN_READINGS * 1000;
          }
        }
      }
      paddedReadings.push(originalReadings[i]);
    }

    return paddedReadings;
  }

  // Apply heuristics to session
  const analyseSession = readings => {
    let sessionAnnotations = [];

    let firstZeroCurrentIndex = null;
    let firstZeroCurrentTimestamp = null;
    let startedChargingTimestamp = null;
    let firstZeroCurrentDisplayTimestamp = null;

    let fifteenMinuteTimeMarker = null;
    let highest = 0;

    let l1Phase = 0;
    let l2Phase = 0;
    let l3Phase = 0;

    for (let i = 0; i < readings.length; ++i) {
      if (readings[i].l1_current) l1Phase = 1;
      if (readings[i].l2_current) l2Phase = 1;
      if (readings[i].l3_current) l3Phase = 1;
      const numPhases = (l1Phase + l2Phase + l3Phase) > 1 ? 3 : 1;

      const total = readings[i].l1_current +
        readings[i].l2_current +
        readings[i].l3_current;

      const zeroReading = total === 0;

      const start = moment(readings[i].timestamp, 'YYYY-MM-DDTHH:mm:ss,Z');

      // Every 5 minutes, check if we can reduce the charge profile
      if (!fifteenMinuteTimeMarker) {
        fifteenMinuteTimeMarker = start;
      } else {
        const elapsedMilliseconds = moment.duration(start.diff(fifteenMinuteTimeMarker))._milliseconds;
        if (elapsedMilliseconds >= FIVE_MINUTES * 1000) {
          fifteenMinuteTimeMarker = start;
          highest = 0;
        }
      }

      if (total > highest) highest = total;

      // Started charging?
      if (!zeroReading) {
        if (!startedChargingTimestamp) {
          startedChargingTimestamp = start;

          sessionAnnotations.push({
            type: 'line',
            borderColor: 'lightBlue',
            borderWidth: 10,
            label: {
              display: false,
              position: 'start',
              backgroundColor: 'blue',
              drawTime: 'afterDatasetsDraw',
              content: [`Charging started at ${dbUTCDatetimeToLocalTime(readings[i].timestamp)}`],
            },
            scaleID: 'x',
            value: i,
            // For simple property changes, you can directly modify the annotation
            // element's properties then return true to force chart re-drawing. This is faster.
            enter({element}, event) {
              element.label.options.display = true;
              return true;
            },
            leave({element}, event) {
              element.label.options.display = false;
              return true;
            }
          });
        }
      }

      // Stopped charging?
      if (startedChargingTimestamp) {
        if (zeroReading) {
          if (!firstZeroCurrentTimestamp) {
            firstZeroCurrentIndex = i;
            firstZeroCurrentTimestamp = start;
            firstZeroCurrentDisplayTimestamp = readings[i].timestamp;
          }

          const elapsedMilliseconds = moment.duration(start.diff(firstZeroCurrentTimestamp))._milliseconds;
          if (
            elapsedMilliseconds >= CHARGE_END_TIMEOUT * 1000 &&
            annotations.length === 0
          ) {
            sessionAnnotations.push({
              type: 'line',
              borderColor: 'lightBlue',
              borderWidth: 10,
              label: {
                display: false,
                position: 'start',
                backgroundColor: 'blue',
                drawTime: 'afterDatasetsDraw',
                content: [`Charging ended at ${dbUTCDatetimeToLocalTime(firstZeroCurrentDisplayTimestamp)}`],
              },
              scaleID: 'x',
              value: firstZeroCurrentIndex,
              // For simple property changes, you can directly modify the annotation
              // element's properties then return true to force chart re-drawing. This is faster.
              enter({element}, event) {
                element.label.options.display = true;
                return true;
              },
              leave({element}, event) {
                element.label.options.display = false;
                return true;
              }
            });

            // {
            //   type: 'point',
            //   xValue: firstZeroCurrentIdx,
            //   yValue: 1,
            //   radius: 5,
            //   backgroundColor: 'rgba(55, 117, 181, 255)'
            // },
            // {
            //   type: 'label',
            //   xValue: firstZeroCurrentIdx + 10,
            //   yValue: 16.3,
            //   position: 'start',
            //   backgroundColor: 'rgba(245,245,245)',
            //   content: [`Charging ended ${firstZeroCurrentTime}`],
            //   font: {
            //     size: 14
            //   }
            // },
            // {
            //   type: 'line',
            //   borderColor: 'black',
            //   borderDash: [6, 6],
            //   borderDashOffset: 0,
            //   borderWidth: 3,
            //   label: {
            //     display: true,
            //     content: (ctx) => 'Average: ' + average(ctx).toFixed(2),
            //     position: 'end'
            //   },
            //   scaleID: 'y',
            //   value: (ctx) => average(ctx)
            // },
            // {
            //   type: 'line',
            //   borderColor: 'lightGreen',
            //   borderWidth: 10,
            //   label: {
            //     display: false,
            //     backgroundColor: 'green',
            //     drawTime: 'afterDatasetsDraw',
            //     content: (ctx) => ['Average of dataset', 'is: ' + average(ctx).toFixed(3)]
            //   },
            //   scaleID: 'y',
            //   value: (ctx) => average(ctx),
            //   // For simple property changes, you can directly modify the annotation
            //   // element's properties then return true to force chart re-drawing. This is faster.
            //   enter({element}, event) {
            //     element.label.options.display = true;
            //     return true;
            //   },
            //   leave({element}, event) {
            //     element.label.options.display = false;
            //     return true;
            //   }
            // }

            break;
          }
        } else {
          firstZeroCurrentTimestamp = null;
        }
      }
    }

    setAnnotations(sessionAnnotations);
  }

  useEffect(() => {
    let longestSessionId = null;
    let longestSessionLength = 0;
    let longestSessionReadings = null;

    let data = [];
    let idx = 0;

    for (var sessionId of Object.keys(sessions)) {
      // TDTODO find session with most readings and use that as xLabels scale

      sessions[sessionId].readings = padSession(sessions[sessionId].readings, highlightMissingReadings);

      const readings = sessions[sessionId].readings;
      const arbitration = sessions[sessionId].arbitration;
      sessions[sessionId].arbitrated_limits = [];

      const SMARTSTEP_VOLTAGE = 230; // SmartSTEP is single phase
      const MAX_SPIKE = 60;

      let a_idx = 0;
      let arbitrated_limit = 0;

      for (let r_idx = 1; r_idx < readings.length; ++r_idx) {
        const start = moment(readings[r_idx - 1].timestamp, 'YYYY-MM-DDTHH:mm:ss,Z');
        const end = moment(readings[r_idx].timestamp, 'YYYY-MM-DDTHH:mm:ss,Z');

        if (sessions[sessionId].project === 'SMARTSTEP') {
          // SmartSTEP currents
          const elapsedMilliseconds = moment.duration(end.diff(start))._milliseconds;
          if (!currents && elapsedMilliseconds > 0) {
            // Calculate consumption, converting from Wh to kWh
            const l1_diff = readings[r_idx].l1_energy - readings[r_idx - 1].l1_energy;
            const l2_diff = readings[r_idx].l2_energy - readings[r_idx - 1].l2_energy;
            const l3_diff = readings[r_idx].l3_energy - readings[r_idx - 1].l3_energy;

            readings[r_idx].l1_current = ((1000 * 3600 * l1_diff) / elapsedMilliseconds) / SMARTSTEP_VOLTAGE;
            readings[r_idx].l2_current = ((1000 * 3600 * l2_diff) / elapsedMilliseconds) / SMARTSTEP_VOLTAGE;
            readings[r_idx].l3_current = ((1000 * 3600 * l3_diff) / elapsedMilliseconds) / SMARTSTEP_VOLTAGE;
          }
        }

        // If session has arbitration events, sync them to the readings
        while (a_idx < arbitration.length) {
          const arbitration_timestamp = moment(arbitration[a_idx].timestamp, 'YYYY-MM-DDTHH:mm:ss,Z');
          if (!arbitration_timestamp.isBefore(start)) {
            if (a_idx > 0) a_idx--;
            break;
          }
          arbitrated_limit = arbitration[a_idx].arbitrated_limit;
          a_idx++;
        }

        sessions[sessionId].arbitrated_limits.push(arbitrated_limit);
      }

      analyseSession(readings);

      const sessionLength = sessions[sessionId].readings.length;
      if (sessionLength > longestSessionLength) {
        longestSessionLength = sessionLength;
        longestSessionId = sessionId;
        longestSessionReadings = readings;
      }

      data.push({
        type: 'line',
        label: labels[idx % 8] + 'L1',
        borderColor: colours[idx % 3][0],
        borderWidth: 2,
        borderJoinStyle: 'miter',
        lineTension: 0,
        pointBackgroundColor: '#13C7B4',
        pointRadius: 0,
        fill: false,
        data: readings.map(r => r.l1_current)
      });
      data.push({
        type: 'line',
        label: labels[idx % 8] + 'L2',
        borderColor: colours[idx % 3][1],
        borderWidth: 2,
        borderJoinStyle: 'miter',
        lineTension: 0,
        pointBackgroundColor: '#13C7B4',
        pointRadius: 0,
        fill: false,
        data: readings.map(r => r.l2_current)
      });
      data.push({
        type: 'line',
        label: labels[idx % 8] + 'L3',
        borderColor: colours[idx % 3][2],
        borderWidth: 2,
        borderJoinStyle: 'miter',
        lineTension: 0,
        pointBackgroundColor: '#13C7B4',
        pointRadius: 0,
        fill: false,
        data: readings.map(r => r.l3_current)
      });

      // Arbitration
      data.push({
        type: 'line',
        label: 'Charge Profile (A)',
        borderColor: '#0000ff',
        borderWidth: 2,
        borderJoinStyle: 'miter',
        lineTension: 0,
        pointBackgroundColor: '#0000ff',
        pointRadius: 0,
        fill: false,
        data: sessions[sessionId].arbitrated_limits
      });

      idx++;
    }

    if (longestSessionId) {
      setXLabels(
        idx === 1 ?
          longestSessionReadings.map(r => dbUTCDatetimeToLocalTime(r.timestamp))
        :
          longestSessionReadings.map(
            r => timeDiff(longestSessionReadings[0].timestamp, r.timestamp)
          )
      );
    }

    if (data.length) setDatasets(data)
  }, [sessions, currents, highlightMissingReadings]);

  if (datasets.length === 0) return "Loading...";

  function average(ctx) {
    const values = ctx.chart.data.datasets[0].data;
    return values.reduce((a, b) => a + b, 0) / values.length;
  }

  function min(ctx) {
    const values = ctx.chart.data.datasets[0].data;
    return values.reduce((a, b) => Math.min(a, b), Infinity);
  }

  return (
    <Segment {...props}>
      <div className="cost-spend-chart-segment-chart-wrapper">
        <Grid stackable stretched>
          <Grid.Row />
          <Grid.Row style={{ paddingTop: '0' }}>
            <Grid.Column
              mobile={16}
              tablet={16}
              computer={15}
              textAlign="center"
              style={{ paddingRight: '0' }}
            >
              <Container style={{ marginLeft: '4%', paddingBottom: '10px' }}>
                <Header
                  as="h4"
                  color="black"
                  size="medium"
                  style={{ marginTop: '0px', marginBottom: '0' }}
                >
                  Meter Readings
                </Header>
                <Bar
                  type="bar"
                  id="session-readings-chart"
                  data={{
                    labels: xLabels,
                    datasets: datasets
                  }}
                  options={{
                    scales: {
                      x: {
                        title: {
                          display: true,
                          text: 'Time'
                        },
                        grid: {
                          display: true
                        }
                      },
                      y: {
                        title: {
                          display: true,
                          text: 'Charge rate (A)'
                        },
                        ticks: {
                          callback: value => `${value}`
                        },
                        grid: {
                          display: true
                        }
                      }
                    },
                    plugins: {
                      annotation: {
                        common: {
                          drawTime: 'beforeDatasetsDraw'
                        },
                        annotations: annotations
                      },
                      zoom: {
                        pan: {
                          enabled: true,
                          mode: 'xy',
                          overScaleMode: 'y'
                        },
                        zoom: {
                          wheel: {
                            enabled: true
                          },
                          pinch: {
                            enabled: true
                          },
                          mode: 'xy',
                          overScaleMode: 'y',
                          onZoomComplete({ chart }) {
                            // This update is needed to display up to date zoom level in the title.
                            // Without this, previous zoom level is displayed.
                            // The reason is: title uses the same beforeUpdate hook, and is evaluated before zoom.
                            chart.update('none');
                          }
                        }
                      },
                      legend: {
                        display: true,
                        position: 'bottom',
                        align: 'end',
                        labels: {
                          boxWidth: 12,
                          boxHeight: 10,
                          filter: item => item.text !== 'Forecast'
                        }
                      },
                      tooltips: {
                        callbacks: {
                          label: (
                            { datasetIndex, index },
                            { datasets, labels }
                          ) => {
                            const dataset = datasets[datasetIndex];
                            const label = labels[index];

                            return label === 'HIDDEN'
                              ? null
                              : `${label}: $${dataset.data[index]}`;
                          }
                        }
                      },
                      datalabels: {
                        display: false
                      }
                    },
                    borderJoinStyle: 'miter'
                  }}
                  key={new Date().getTime()}
                  height={50}
                  width={100}
                />
              </Container>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>
    </Segment>
  );
};
